import { useMemo } from "react";

/** A page_content entry as these handlers read it: matched by `variable`, written at `value`. */
type EditableContentEntry = { variable?: string; value?: unknown };

/** The edited page, as far as these handlers read it. */
type EditablePage = { page_content?: EditableContentEntry[] };

/**
 * usePageContentEditors — the bag of `page_content` edit handlers the update
 * screen wires into UpdatePageView, collapsed out of UpdatePageContainer
 * (SCRUM-910). All five write through the given `setPage`; they only differ in
 * how they read the incoming event/value and which slice of the matching
 * variable they replace. Memoized on `setPage` (stable), so identities never
 * churn.
 *
 * The null-handling per handler is preserved verbatim — the sitemap channel
 * guards with `|| []`, the rest use `?.map` — do NOT unify these: they differ
 * only when `page_content` is absent and the pinned behaviour is intentional.
 */
export function usePageContentEditors<P extends EditablePage>(
  setPage: (updater: (prev: P) => P) => void
) {
  return useMemo(() => {
    // Replace the whole matching variable object (by name). Shared write path
    // for the Required/Optional field editors (the two handlers were identical).
    const replaceVariable = (updatedVariable: EditableContentEntry) =>
      setPage((prev) => ({
        ...prev,
        page_content: prev.page_content?.map((c) =>
          c.variable === updatedVariable.variable ? updatedVariable : c
        ),
      }));

    return {
      handleRequiredVariableChange: replaceVariable,
      handleOptionalVariableChange: replaceVariable,

      // SCRUM-869: the Optional sitemap side-channel (HandleContent (optional))
      // reports a { target: { name, value } } envelope, not a bare array. Merge
      // the reported value into the matching page_content entry by variable name
      // (|| [] guard, pinned — see UpdatePageHeaderFooterContainer / SCRUM-853).
      onChangeContentOptional: (e: { target: { name?: string; value: unknown } }) => {
        const { name, value } = e.target;
        setPage((prev) => ({
          ...prev,
          page_content: (prev.page_content || []).map((cv) =>
            cv.variable === name ? { ...cv, value } : cv
          ),
        }));
      },

      onChangeVariableValue: (
        e: { target?: { name: string; value: unknown }; label?: string; value?: unknown } | null,
        action?: { name?: string }
      ) => {
        // String / Number
        const target = e?.target;
        if (target) {
          const name = target.name;
          const val = target.value;
          setPage((prev) => ({
            ...prev,
            page_content: prev.page_content?.map((cv) =>
              cv.variable === name ? { ...cv, value: val } : cv
            ),
          }));
          return;
        }
        // Boolean (react-select)
        if (e?.label && action?.name) {
          const varName = action.name;
          setPage((prev) => ({
            ...prev,
            page_content: prev.page_content?.map((cv) =>
              cv.variable === varName ? { ...cv, value: e.value } : cv
            ),
          }));
        }
      },

      handleLinkInputRequired: (event: { target: { name: string; value: unknown } }, targetVariableName: string) => {
        const key = event.target.name;
        const val = event.target.value;
        setPage((prev) => ({
          ...prev,
          page_content: prev.page_content?.map((cv) =>
            cv.variable === targetVariableName
              ? { ...cv, value: { ...(cv.value as Record<string, unknown> | undefined), [key]: val } }
              : cv
          ),
        }));
      },
    };
  }, [setPage]);
}
